/**
 * Notifications Settings Tab Component
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { 
  Mail, 
  Send, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  AlertTriangle, 
  Bell, 
  Clock,
  CalendarRange,
  FileText
} from 'lucide-react';

export function NotificationsTab({
  notificationConfig,
  expiryPreview,
  notificationHistory,
  testEmailAddress,
  setTestEmailAddress,
  sendingTestEmail,
  sendingAlerts,
  onSendTestEmail,
  onSendExpiryAlerts,
  systemSettings,
  onUpdateSystemSettings,
  sendingDigest,
  onSendDigestNow,
}) {
  return (
    <div className="grid gap-6">
      {/* Email Configuration Status */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardHeader>
          <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#1ABC9C]" />
            Email Configuration
          </CardTitle>
          <CardDescription className="text-[#A1A1AA]">
            Configure and test document expiry notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
            <div className="flex items-center gap-3">
              {notificationConfig?.email_configured ? (
                <CheckCircle className="w-5 h-5 text-[#1ABC9C]" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
              <div>
                <p className="text-[#F2F2F2] font-medium">Email Service (Resend)</p>
                <p className="text-sm text-[#A1A1AA]">
                  {notificationConfig?.email_configured 
                    ? 'Connected and ready to send emails' 
                    : 'Not configured - Add RESEND_API_KEY to environment'
                  }
                </p>
              </div>
            </div>
            <Badge className={notificationConfig?.email_configured 
              ? 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20'
              : 'bg-red-500/15 text-red-400 border-red-500/20'
            }>
              {notificationConfig?.email_configured ? 'Connected' : 'Not Configured'}
            </Badge>
          </div>

          {/* Test Email */}
          <div className="p-4 bg-[#1A1A1A] rounded-lg">
            <p className="text-[#F2F2F2] font-medium mb-3">Send Test Email</p>
            <div className="flex gap-3">
              <Input
                type="email"
                placeholder="Enter email address"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                className="flex-1 bg-[#2B2B2B] border-white/10"
                data-testid="test-email-input"
              />
              <Button
                onClick={onSendTestEmail}
                disabled={sendingTestEmail || !notificationConfig?.email_configured}
                className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                data-testid="send-test-email-btn"
              >
                {sendingTestEmail ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Test
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-[#A1A1AA] mt-2">
              Sends a sample expiry alert email to verify configuration
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Expiry Alert Preview */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#FFC04D]" />
              Documents Requiring Alerts
            </CardTitle>
            <CardDescription className="text-[#A1A1AA]">
              Preview of documents expiring within 30 days
            </CardDescription>
          </div>
          <Button
            onClick={onSendExpiryAlerts}
            disabled={sendingAlerts || !notificationConfig?.email_configured || !expiryPreview?.total}
            className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
            data-testid="send-expiry-alerts-btn"
          >
            {sendingAlerts ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Bell className="w-4 h-4 mr-2" />
                Send Expiry Alerts
              </>
            )}
          </Button>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-[#1A1A1A] rounded-lg text-center">
              <p className="text-2xl font-bold text-[#F2F2F2]">{expiryPreview?.total || 0}</p>
              <p className="text-xs text-[#A1A1AA]">Total Expiring</p>
            </div>
            <div className="p-4 bg-red-500/10 rounded-lg text-center border border-red-500/20">
              <p className="text-2xl font-bold text-red-400">{expiryPreview?.summary?.critical || 0}</p>
              <p className="text-xs text-red-400">Critical (≤7 days)</p>
            </div>
            <div className="p-4 bg-[#FFC04D]/10 rounded-lg text-center border border-[#FFC04D]/20">
              <p className="text-2xl font-bold text-[#FFC04D]">{expiryPreview?.summary?.warning || 0}</p>
              <p className="text-xs text-[#FFC04D]">Warning (8-14 days)</p>
            </div>
            <div className="p-4 bg-blue-500/10 rounded-lg text-center border border-blue-500/20">
              <p className="text-2xl font-bold text-blue-400">{expiryPreview?.summary?.upcoming || 0}</p>
              <p className="text-xs text-blue-400">Upcoming (15-30 days)</p>
            </div>
          </div>

          {/* Documents List */}
          {expiryPreview?.documents?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-[#A1A1AA]">Medic</TableHead>
                  <TableHead className="text-[#A1A1AA]">Document</TableHead>
                  <TableHead className="text-[#A1A1AA]">Expires</TableHead>
                  <TableHead className="text-[#A1A1AA]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expiryPreview.documents.slice(0, 10).map((doc, idx) => (
                  <TableRow key={idx} className="border-white/5 hover:bg-white/5">
                    <TableCell className="text-[#F2F2F2]">{doc.medic_name}</TableCell>
                    <TableCell className="text-[#A1A1AA]">{doc.document_type_name}</TableCell>
                    <TableCell className="text-[#A1A1AA]">{doc.expires_at?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <Badge className={
                        doc.days_until_expiry <= 7 
                          ? 'bg-red-500/15 text-red-400 border-red-500/20'
                          : doc.days_until_expiry <= 14
                          ? 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20'
                          : 'bg-blue-500/15 text-blue-400 border-blue-500/20'
                      }>
                        {doc.days_until_expiry} days
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-[#A1A1AA]">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-[#1ABC9C]" />
              <p>No documents expiring within 30 days</p>
            </div>
          )}

          {expiryPreview?.total > 10 && (
            <p className="text-sm text-[#A1A1AA] mt-4 text-center">
              Showing 10 of {expiryPreview.total} documents. Alerts will be sent for all.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Weekly Digest */}
      {onUpdateSystemSettings && (
        <Card className="bg-[#2B2B2B] border-white/5" data-testid="weekly-digest-card">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
              <CalendarRange className="w-5 h-5 text-[#1ABC9C]" />
              Weekly Digest
            </CardTitle>
            <CardDescription className="text-[#A1A1AA]">
              A Monday-morning summary of the week ahead for managers.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
              <div>
                <p className="text-[#F2F2F2] font-medium">Enable weekly digest</p>
                <p className="text-sm text-[#A1A1AA]">
                  Scheduled every Monday at 07:00 local time. Toggle off to pause without losing recipients.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={systemSettings?.weekly_digest_enabled ?? true}
                  onCheckedChange={(checked) => onUpdateSystemSettings('weekly_digest_enabled', checked)}
                  data-testid="weekly-digest-toggle"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/10 text-[#F2F2F2] hover:bg-white/5"
                  onClick={onSendDigestNow}
                  disabled={sendingDigest}
                  data-testid="send-digest-now-btn"
                >
                  {sendingDigest ? 'Sending…' : 'Send now'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alert Schedule Info */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardHeader>
          <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#1ABC9C]" />
            Alert Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-[#1A1A1A] rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <p className="text-[#F2F2F2] font-medium">7 Days</p>
              </div>
              <p className="text-sm text-[#A1A1AA]">Critical reminder sent</p>
            </div>
            <div className="p-4 bg-[#1A1A1A] rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-[#FFC04D]"></div>
                <p className="text-[#F2F2F2] font-medium">14 Days</p>
              </div>
              <p className="text-sm text-[#A1A1AA]">Warning reminder sent</p>
            </div>
            <div className="p-4 bg-[#1A1A1A] rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                <p className="text-[#F2F2F2] font-medium">30 Days</p>
              </div>
              <p className="text-sm text-[#A1A1AA]">Initial notice sent</p>
            </div>
          </div>
          <p className="text-sm text-[#A1A1AA] mt-4">
            <strong>Recipients:</strong> Medics receive alerts about their own documents. Admins receive a platform-wide digest.
          </p>
          <p className="text-sm text-[#A1A1AA] mt-2">
            <strong>GDPR/NHS Compliance:</strong> Emails contain no sensitive medical data - only document type and expiry date with link to secure portal.
          </p>
        </CardContent>
      </Card>

      {/* Notification History */}
      {notificationHistory?.length > 0 && (
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1ABC9C]" />
              Recent Notification Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {notificationHistory.slice(0, 5).map((log, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg">
                  <div className="flex items-center gap-3">
                    <Bell className="w-4 h-4 text-[#A1A1AA]" />
                    <div>
                      <p className="text-sm text-[#F2F2F2]">{log.action.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-[#A1A1AA]">by {log.user_name}</p>
                    </div>
                  </div>
                  <p className="text-xs text-[#A1A1AA]">
                    {new Date(log.timestamp).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
